import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Eye, EyeOff, User, Mail, Lock, Sparkles } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_obsidian-tier/artifacts/za07r3ra_dungeon-eyes-wide-shut-logo-bigger-text.png";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_obsidian-tier/artifacts/lzvj70hg_bdsm-2_jpg.webp";

// Provocative captions from the website
const CAPTIONS = [
  "Surrender Yourself To Your Dark Desires",
  "A Private Affair Impossible to Forget",
  "Unlock Your Hidden Desires",
  "Welcome to Heaven",
  "Escape to Sensual Seclusion"
];

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captionIndex, setCaptionIndex] = useState(0);
  const { login, register, member } = useAuth();
  const navigate = useNavigate();

  // Rotate captions
  useEffect(() => {
    const interval = setInterval(() => {
      setCaptionIndex((prev) => (prev + 1) % CAPTIONS.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Redirect if already logged in
  if (member) {
    navigate(member.is_admin ? "/admin" : "/dashboard");
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let loggedInMember;
      if (isLogin) {
        loggedInMember = await login(email, password);
        toast.success("Welcome back to the shadows");
      } else {
        loggedInMember = await register(email, password, displayName);
        toast.success("Your journey into darkness begins... Check your email");
      }
      navigate(loggedInMember.is_admin ? "/admin" : "/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Hero Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#050505]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
        
        {/* Animated Caption Overlay */}
        <div className="absolute bottom-20 left-10 right-10 z-10">
          <p 
            key={captionIndex}
            className="font-cinzel text-3xl text-white leading-tight animate-fade-in-up"
            style={{ textShadow: '2px 2px 20px rgba(0,0,0,0.8)' }}
          >
            {CAPTIONS[captionIndex]}
          </p>
          <div className="flex gap-2 mt-6">
            {CAPTIONS.map((_, i) => (
              <div 
                key={i}
                className={`h-1 rounded-full transition-all duration-500 ${
                  i === captionIndex ? 'w-8 bg-[#b30000]' : 'w-2 bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 bg-[#050505] flex flex-col justify-center px-8 lg:px-16 py-12 relative">
        {/* Mobile background */}
        <div 
          className="absolute inset-0 lg:hidden opacity-20 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0 lg:hidden bg-[#050505]/90" />
        
        <div className="relative z-10 max-w-md mx-auto w-full">
          {/* Logo */}
          <div className="text-center mb-10">
            <img 
              src={LOGO_URL} 
              alt="Dungeon Eyes Wide Shut" 
              className="w-56 h-auto mx-auto mb-6 animate-float"
              data-testid="login-logo"
            />
            <h1 className="font-cinzel text-xl tracking-[0.2em] uppercase" style={{ color: '#c5a059' }}>
              Loyalty Shadow Membership
            </h1>
            <p className="mt-2 text-sm">
              <span className="text-[#b30000] font-semibold">Every 5 Visits</span>
              <span className="text-[#c5a059]/60"> = 1 Free Evening Session</span>
            </p>
          </div>

          {/* Provocative tagline - mobile only */}
          <div className="lg:hidden text-center mb-8">
            <p 
              key={captionIndex}
              className="font-cinzel text-lg text-[#c5a059] animate-fade-in-up"
            >
              {CAPTIONS[captionIndex]}
            </p>
          </div>

          {/* Auth Card */}
          <div className="glass-obsidian rounded-xl p-8" data-testid="auth-card">
            {/* Tabs */}
            <div className="flex gap-2 mb-8">
              <button
                onClick={() => setIsLogin(true)}
                className={`flex-1 py-3 font-cinzel text-sm tracking-[0.15em] uppercase transition-all duration-300 rounded-lg ${
                  isLogin 
                    ? 'bg-[#8a0303] text-white shadow-[0_0_25px_rgba(138,3,3,0.5)]' 
                    : 'bg-[#0a0a0a] text-[#c5a059]/50 hover:text-[#c5a059] border border-[#c5a059]/10'
                }`}
                data-testid="login-tab"
              >
                Enter
              </button>
              <button
                onClick={() => setIsLogin(false)}
                className={`flex-1 py-3 font-cinzel text-sm tracking-[0.15em] uppercase transition-all duration-300 rounded-lg ${
                  !isLogin 
                    ? 'bg-[#8a0303] text-white shadow-[0_0_25px_rgba(138,3,3,0.5)]' 
                    : 'bg-[#0a0a0a] text-[#c5a059]/50 hover:text-[#c5a059] border border-[#c5a059]/10'
                }`}
                data-testid="register-tab"
              >
                Join Us Free
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div className="relative group">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#c5a059]/40 group-focus-within:text-[#c5a059] transition-colors" />
                  <input
                    type="text"
                    placeholder="Your Shadow Name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    required={!isLogin}
                    className="w-full bg-[#0a0a0a] border border-[#c5a059]/20 text-[#e5c07b] placeholder:text-[#c5a059]/30 
                      focus:border-[#c5a059] focus:ring-1 focus:ring-[#c5a059]/50 rounded-lg h-14 pl-12 pr-4 font-manrope
                      transition-all duration-300"
                    data-testid="display-name-input"
                  />
                </div>
              )}

              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#c5a059]/40 group-focus-within:text-[#c5a059] transition-colors" />
                <input
                  type="email"
                  placeholder="Discreet Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full bg-[#0a0a0a] border border-[#c5a059]/20 text-[#e5c07b] placeholder:text-[#c5a059]/30 
                    focus:border-[#c5a059] focus:ring-1 focus:ring-[#c5a059]/50 rounded-lg h-14 pl-12 pr-4 font-manrope
                    transition-all duration-300"
                  data-testid="email-input"
                />
              </div>

              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#c5a059]/40 group-focus-within:text-[#c5a059] transition-colors" />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Secret Passphrase"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full bg-[#0a0a0a] border border-[#c5a059]/20 text-[#e5c07b] placeholder:text-[#c5a059]/30 
                    focus:border-[#c5a059] focus:ring-1 focus:ring-[#c5a059]/50 rounded-lg h-14 pl-12 pr-12 font-manrope
                    transition-all duration-300"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#c5a059]/40 hover:text-[#c5a059] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#8a0303] to-[#b30000] hover:from-[#b30000] hover:to-[#d40000] 
                  text-white font-cinzel tracking-[0.15em] uppercase py-4 rounded-lg transition-all duration-300 
                  animate-glow-pulse disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                data-testid="submit-button"
              >
                {loading ? (
                  <span className="animate-pulse">Entering...</span>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    {isLogin ? "Step Into Darkness" : "Begin Your Journey"}
                  </>
                )}
              </button>
            </form>

            {!isLogin && (
              <p className="text-center mt-4 text-[#c5a059]/50 text-xs">
                Welcome email will be sent upon registration
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="text-center mt-8">
            <p className="text-[#c5a059]/30 text-xs tracking-[0.15em]">
              Discretion Absolute. Your Secrets Are Safe.
            </p>
            <p className="text-[#c5a059]/20 text-xs mt-2">
              dungeon-eyeswideshut.co.uk
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
